import { type Ref, computed, isRef, onScopeDispose, ref, watch } from "vue";
import { i18n } from "@/app/translates/translates";
import { message$ } from "@/entities/tech";
import { online$ } from "./cache.events";
import { deleteCache, getCache, isCacheEntryStale, setCache } from "./cache.lib";
import type { CachedResourceSource, ICacheEntry, IUseCachedResourceOptions } from "./cache.types";

// Tell the user, once per offline stretch, that what they're seeing was served
// from the on-device cache rather than the network. Many resources can hydrate
// from cache on a single screen, so we throttle to one message and re-arm it
// when connectivity returns.
let offlineCacheNotified = false;
online$.subscribe(
  (online) => {
    if (online) offlineCacheNotified = false;
  },
  { onlyChanges: true },
);

function notifyOfflineCacheUsed() {
  if (offlineCacheNotified) return;
  offlineCacheNotified = true;
  message$.next({
    type: "warning",
    text: i18n.global.t(
      "cache.offlineCached",
      {},
      { default: "Нет интернета. Показаны сохранённые данные." },
    ),
  });
}

export interface ICachedResource<T> {
  data: Ref<T | null>;
  isLoading: Ref<boolean>;
  error: Ref<unknown>;
  source: Ref<CachedResourceSource>;
  isStale: Ref<boolean>;
  isOffline: Ref<boolean>;
  refresh: (options?: { force?: boolean }) => Promise<void>;
  invalidate: () => Promise<void>;
}

/**
 * Reusable cache-aware fetcher. Hydrates from IndexedDB synchronously when
 * available, then revalidates over the network unless offline or fresh.
 * Pass a ref for `key` to react to identifier changes (e.g. route params).
 */
export function useCachedResource<T>(options: IUseCachedResourceOptions<T>): ICachedResource<T> {
  const data = ref<T | null>(null) as Ref<T | null>;
  const isLoading = ref(false);
  const error = ref<unknown>(null);
  const source = ref<CachedResourceSource>("none");
  const isStale = ref(false);
  const isOffline = ref(!online$.value);

  const keyRef = computed(() => (isRef(options.key) ? options.key.value : options.key));
  let controller: AbortController | null = null;

  function applyCached(cached: ICacheEntry<T> | undefined) {
    if (!cached) return;
    data.value = cached.data;
    source.value = "cache";
    isStale.value = isCacheEntryStale(cached, options.ttl);
  }

  async function fetchFresh(key: string, cached: ICacheEntry<T> | undefined) {
    controller?.abort();
    controller = new AbortController();
    isLoading.value = true;
    try {
      const fresh = await options.fetcher(controller.signal);
      data.value = fresh;
      source.value = "network";
      isStale.value = false;
      await setCache(options.namespace, key, fresh, { ttl: options.ttl });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      if (options.strategy === "network-first" && cached) {
        applyCached(cached);
        return;
      }
      error.value = err;
      options.onError?.(err);
    } finally {
      isLoading.value = false;
    }
  }

  async function load(loadOptions: { force?: boolean } = {}) {
    const key = keyRef.value;
    if (!key) {
      data.value = null;
      source.value = "none";
      isStale.value = false;
      return;
    }

    error.value = null;

    const strategy = options.strategy ?? "cache-first";
    const cached = await getCache<T>(options.namespace, key);
    const offline = !online$.value;
    isOffline.value = offline;

    if (strategy === "network-first") {
      if (offline) {
        applyCached(cached);
        if (cached) notifyOfflineCacheUsed();
        else isStale.value = true;
        return;
      }
      await fetchFresh(key, cached);
      return;
    }

    if (strategy === "stale-while-revalidate") {
      applyCached(cached);
      if (!cached) isStale.value = true;
      if (offline) {
        if (cached) notifyOfflineCacheUsed();
        return;
      }
      await fetchFresh(key, cached);
      return;
    }

    applyCached(cached);
    if (!cached) isStale.value = true;
    if (offline) {
      if (cached) notifyOfflineCacheUsed();
      return;
    }
    if (!loadOptions.force && cached && !isStale.value) return;

    await fetchFresh(key, cached);
  }

  async function invalidate() {
    const key = keyRef.value;
    if (!key) return;
    await deleteCache(options.namespace, key);
    isStale.value = true;
  }

  watch(keyRef, () => void load(), { immediate: options.immediate !== false });

  const unsubscribeOnline = online$.subscribe(
    (value) => {
      isOffline.value = !value;
      if (value && isStale.value) void load();
    },
    { onlyChanges: true },
  );

  onScopeDispose(() => {
    controller?.abort();
    unsubscribeOnline();
  });

  return {
    data,
    isLoading,
    error,
    source,
    isStale,
    isOffline,
    refresh: load,
    invalidate,
  };
}
